import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { userDisplayNameAsString, userAbbreviatedName } from '../../util/data';
import { getAdvertiserCoverUrl } from '../../util/advertiserBranding';
import { Page, ResponsiveImage, IconSpinner } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { loadData } from './AdvertisersPage.duck';
import officeLounge from '../../assets/homepage/office-lounge-768.webp';
import css from './AdvertisersPage.module.css';

const LOGO_VARIANTS = ['square-small', 'square-small2x'];
const COVER_VARIANTS = ['landscape-crop', 'landscape-crop2x'];
const normalizeName = value =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr')
    .trim();
const hasVariants = (image, variants) =>
  variants.some(key => image?.attributes?.variants?.[key]?.url);
const Arrow = () => <span aria-hidden="true">↗</span>;
const SearchIcon = () => (
  <svg
    className={css.searchIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    aria-hidden="true"
  >
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m16 16 4 4" />
  </svg>
);

const AdvertiserCard = ({ user, reference }) => {
  const name = userDisplayNameAsString(user, 'Annonceur');
  const customCover = getAdvertiserCoverUrl(user);
  const listingCover = reference?.listingsPreview?.find(listing =>
    hasVariants(listing.firstImage, COVER_VARIANTS)
  )?.firstImage;
  const count = reference?.listingsCount || 0;
  return (
    <article className={css.card}>
      <Link to={`/u/${user.id.uuid}`} className={css.cardLink}>
        <div className={css.cover}>
          {customCover ? (
            <img src={customCover} alt="" loading="lazy" width="800" height="533" />
          ) : listingCover ? (
            <ResponsiveImage
              alt=""
              image={listingCover}
              variants={COVER_VARIANTS}
              sizes="(max-width: 600px) 100vw, (max-width: 950px) 50vw, 33vw"
              loading="lazy"
              width="800"
              height="533"
            />
          ) : (
            <span className={css.coverPattern} aria-hidden="true" />
          )}
        </div>
        <div className={css.cardBody}>
          <div className={css.logo}>
            {hasVariants(user.profileImage, LOGO_VARIANTS) ? (
              <ResponsiveImage
                alt={`Logo de ${name}`}
                image={user.profileImage}
                variants={LOGO_VARIANTS}
                sizes="56px"
                loading="lazy"
                width="56"
                height="56"
              />
            ) : (
              <span aria-hidden="true">{userAbbreviatedName(user, name.slice(0, 2))}</span>
            )}
          </div>
          <h3>{name}</h3>
          <p className={css.listingCount}>
            <span className={css.statusDot} aria-hidden="true" />
            {count} annonce{count > 1 ? 's' : ''} publiée{count > 1 ? 's' : ''}
          </p>
          <div className={css.cardAction}>
            Découvrir la vitrine <Arrow />
          </div>
        </div>
      </Link>
    </article>
  );
};

export const AdvertisersPageComponent = ({
  scrollingDisabled,
  advertisers = [],
  advertisersRefs = [],
  fetchInProgress,
  fetchAdvertisersError,
}) => {
  const config = useConfiguration();
  const intl = useIntl();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('active');
  const directoryRef = useRef(null);
  const searchRef = useRef(null);
  const exploreDirectory = event => {
    event.preventDefault();
    directoryRef.current?.scrollIntoView({ block: 'start' });
    searchRef.current?.focus({ preventScroll: true });
  };
  const refsById = useMemo(() => new Map(advertisersRefs.map(ref => [ref.id.uuid, ref])), [
    advertisersRefs,
  ]);
  const filteredAdvertisers = useMemo(() => {
    const search = normalizeName(query);
    return advertisers
      .filter(user => normalizeName(userDisplayNameAsString(user, '')).includes(search))
      .sort((a, b) => {
        const names = userDisplayNameAsString(a, '').localeCompare(
          userDisplayNameAsString(b, ''),
          'fr',
          { sensitivity: 'base' }
        );
        return sort === 'name'
          ? names
          : (refsById.get(b.id.uuid)?.listingsCount || 0) -
              (refsById.get(a.id.uuid)?.listingsCount || 0) || names;
      });
  }, [advertisers, refsById, query, sort]);
  const count = advertisers.length;
  const totalListings = advertisers.reduce(
    (total, user) => total + (refsById.get(user.id.uuid)?.listingsCount || 0),
    0
  );
  const ready = !fetchInProgress && !fetchAdvertisersError;
  const title = intl.formatMessage(
    { id: 'AdvertisersPage.schemaTitle' },
    { marketplaceName: config.marketplaceName }
  );
  return (
    <Page
      title={title}
      description="Découvrez les annonceurs de Cobuco, leurs bureaux à louer ou à vendre et leurs espaces de coworking. Consultez leurs vitrines et contactez-les directement."
      canonicalPath="/annonceurs"
      scrollingDisabled={scrollingDisabled}
      className={css.root}
      schema={{
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: title,
        url: `${config.marketplaceRootURL}/annonceurs`,
      }}
    >
      <div className={css.topbar}>
        <TopbarContainer currentPage="AdvertisersPage" />
      </div>
      <main className={css.content} id="main">
        <div className={css.container}>
          <nav className={css.breadcrumb} aria-label="Fil d’Ariane">
            <Link to="/">Accueil</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">Nos annonceurs</span>
          </nav>
          <section className={css.hero} aria-labelledby="advertisers-title">
            <div className={css.heroCopy}>
              <p className={css.eyebrow}>LE RÉSEAU COBUCO</p>
              <h1 id="advertisers-title">
                Nos annonceurs.
                <br />
                <span>Vos prochains contacts.</span>
              </h1>
              <p className={css.intro}>
                Derrière chaque espace, une équipe à rencontrer. Découvrez leurs lieux, explorez
                leurs vitrines et donnez une nouvelle adresse à votre projet.
              </p>
              <a href="#annuaire" className={css.primaryButton} onClick={exploreDirectory}>
                Explorer les annonceurs <Arrow />
              </a>
              {ready && count > 0 && (
                <p className={css.networkCount}>
                  <strong>{count}</strong> annonceur{count > 1 ? 's' : ''}
                  <span aria-hidden="true">·</span>
                  <strong>{totalListings}</strong> annonce{totalListings > 1 ? 's' : ''} publiée
                  {totalListings > 1 ? 's' : ''}
                </p>
              )}
            </div>
            <div className={css.heroVisual}>
              <img src={officeLounge} alt="" width="768" height="512" fetchpriority="high" />
              <div className={css.photoCaption}>
                <span className={css.captionIcon} aria-hidden="true">
                  ↗
                </span>
                <p>
                  Des lieux à découvrir.
                  <br />
                  <strong>Des projets à construire.</strong>
                </p>
              </div>
            </div>
          </section>
          <section
            className={css.directory}
            id="annuaire"
            ref={directoryRef}
            aria-labelledby="directory-title"
            aria-busy={fetchInProgress || false}
          >
            <div className={css.sectionHeading}>
              <div>
                <p className={css.eyebrow}>FAITES CONNAISSANCE</p>
                <h2 id="directory-title">Trouvez votre prochain interlocuteur.</h2>
              </div>
              <p>
                Leurs espaces, leur univers.
                <br />À vous de trouver le vôtre.
              </p>
            </div>
            <div className={css.toolbar}>
              <div className={css.searchField}>
                <SearchIcon />
                <label className={css.srOnly} htmlFor="advertiser-search">
                  Rechercher un annonceur
                </label>
                <input
                  id="advertiser-search"
                  ref={searchRef}
                  type="search"
                  placeholder="Rechercher un annonceur…"
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  aria-controls="advertiser-results"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label="Effacer la recherche"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className={css.sortField}>
                <label htmlFor="advertiser-sort">Trier par</label>
                <select
                  id="advertiser-sort"
                  value={sort}
                  onChange={event => setSort(event.target.value)}
                >
                  <option value="active">Nombre d’annonces</option>
                  <option value="name">Nom de A à Z</option>
                </select>
              </div>
            </div>
            <div id="advertiser-results">
              {fetchInProgress ? (
                <div className={css.state} role="status">
                  <IconSpinner />
                  <p>Chargement des annonceurs…</p>
                </div>
              ) : fetchAdvertisersError ? (
                <div className={css.state} role="alert">
                  <h3>Les annonceurs sont momentanément indisponibles.</h3>
                  <p>Réessayez dans un instant ou découvrez les espaces déjà en ligne.</p>
                  <Link to="/annonces" className={css.textLink}>
                    Voir les annonces <Arrow />
                  </Link>
                </div>
              ) : (
                <>
                  <p className={css.resultCount} role="status" aria-live="polite">
                    {filteredAdvertisers.length} annonceur
                    {filteredAdvertisers.length > 1 ? 's' : ''}
                    {normalizeName(query)
                      ? ` trouvé${filteredAdvertisers.length > 1 ? 's' : ''}`
                      : ' à découvrir'}
                  </p>
                  {filteredAdvertisers.length ? (
                    <div className={css.grid}>
                      {filteredAdvertisers.map(user => (
                        <AdvertiserCard
                          key={user.id.uuid}
                          user={user}
                          reference={refsById.get(user.id.uuid)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={css.state}>
                      <SearchIcon />
                      <h3>
                        {count
                          ? 'Aucun annonceur ne correspond à votre recherche.'
                          : 'Le réseau se prépare à vous accueillir.'}
                      </h3>
                      <p>
                        {count
                          ? 'Essayez un autre nom ou retrouvez tous les annonceurs.'
                          : 'Votre entreprise pourrait être la prochaine à rejoindre Cobuco.'}
                      </p>
                      {count > 0 && (
                        <button
                          type="button"
                          className={css.primaryButton}
                          onClick={() => setQuery('')}
                        >
                          Voir tous les annonceurs
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
          <section className={css.joinSection} aria-labelledby="join-title">
            <div>
              <p className={css.eyebrow}>ET SI ON VOUS RENCONTRAIT ?</p>
              <h2 id="join-title">
                Votre entreprise a aussi
                <br />
                sa place ici.
              </h2>
              <p>
                Votre logo, vos photos, vos espaces. Créez une vitrine à votre image et présentez
                vos biens à louer ou à vendre.
              </p>
            </div>
            <div className={css.joinActions}>
              <Link to="/l/new" className={css.primaryButton}>
                Déposer une annonce <Arrow />
              </Link>
              <Link to="/profile-settings" className={css.textLink}>
                Personnaliser ma vitrine <Arrow />
              </Link>
            </div>
          </section>
          <details className={css.visibilityHelp}>
            <summary>
              Comment apparaître dans l’annuaire des annonceurs ? <span aria-hidden="true">+</span>
            </summary>
            <p>
              Votre entreprise apparaît ici dès qu’au moins une de ses annonces est publiée. Les
              brouillons et les annonces en attente de validation ne sont pas encore visibles.{' '}
              <Link to="/listings?tab=listings">Vérifier mes annonces</Link>
            </p>
          </details>
        </div>
      </main>
      <FooterContainer />
    </Page>
  );
};

const mapStateToProps = state => {
  const { advertisersRefs, fetchInProgress, fetchAdvertisersError } = state.AdvertisersPage;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    advertisers: getMarketplaceEntities(
      state,
      advertisersRefs.map(ref => ({ type: 'user', id: ref.id }))
    ),
    advertisersRefs,
    fetchInProgress,
    fetchAdvertisersError,
  };
};
const AdvertisersPage = connect(mapStateToProps)(AdvertisersPageComponent);
AdvertisersPage.loadData = loadData;
export default AdvertisersPage;
